import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useMyFarm, useFarmTickets, useUpdateTicket } from '../api/hooks'
import type { Ticket } from '@horsehub/shared'
import { PageSpinner, EmptyState, Spinner } from '../components/ui'
import { fmtDate } from '../lib/format'

export function FarmTickets() {
  const { t, locale } = useI18n()
  const nav = useNavigate()
  const { data: farm, isLoading } = useMyFarm()
  const { data: tickets, isLoading: ticketsLoading } = useFarmTickets(farm?.id)
  const update = useUpdateTicket(farm?.id ?? '')
  const [filter, setFilter] = useState<'open' | 'all'>('open')

  if (isLoading) return <PageSpinner />
  if (!farm) {
    nav('/farm/onboarding', { replace: true })
    return null
  }

  const list = (tickets ?? []).filter((tk) => filter === 'all' || tk.status === 'open')

  return (
    <main className="mx-auto max-w-3xl px-5 py-8">
      <Link to="/farm" className="text-sm font-medium text-brand-600 dark:text-brand-300 hover:underline">{t('farm.horse.back')}</Link>

      <div className="mb-6 mt-3 flex items-center justify-between">
        <h1 className="text-2xl font-extrabold tracking-tight">{t('tickets.dashTitle')}</h1>
        <div className="flex gap-1 rounded-full bg-brand-100 dark:bg-brand-800 p-1 text-sm">
          {(['open', 'all'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1.5 font-semibold transition ${filter === f ? 'bg-white dark:bg-brand-700 shadow-soft' : 'text-brand-600 dark:text-brand-300'}`}
            >
              {t(`tickets.${f}`)}
            </button>
          ))}
        </div>
      </div>

      {ticketsLoading ? (
        <div className="grid place-items-center py-12"><Spinner /></div>
      ) : list.length === 0 ? (
        <EmptyState icon="📋" title={t('tickets.noneOwner')} />
      ) : (
        <div className="space-y-4">
          {list.map((tk) => (
            <TicketCard
              key={tk.id}
              ticket={tk}
              locale={locale}
              busy={update.isPending}
              onResolve={() => update.mutate({ tid: tk.id, status: 'resolved' })}
              onReopen={() => update.mutate({ tid: tk.id, status: 'open' })}
            />
          ))}
        </div>
      )}
    </main>
  )
}

export function TicketCard({
  ticket, locale, onResolve, onReopen, busy,
}: {
  ticket: Ticket
  locale: string
  onResolve?: () => void
  onReopen?: () => void
  busy?: boolean
}) {
  const { t } = useI18n()
  const resolved = ticket.status === 'resolved'
  return (
    <div className={`card p-5 ${resolved ? 'opacity-70' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {ticket.priority === 'urgent' && !resolved && (
            <span className="chip !bg-red-100 !text-red-700 dark:!bg-red-900/40 dark:!text-red-300">{t('tickets.prio.urgent')}</span>
          )}
          <h3 className="text-lg font-bold">{ticket.subject}</h3>
        </div>
        <span className={`chip ${resolved ? '!bg-emerald-100 !text-emerald-700 dark:!bg-emerald-900/40 dark:!text-emerald-300' : ''}`}>
          {t(`tickets.st.${ticket.status}`)}
        </span>
      </div>

      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-brand-500 dark:text-brand-400">
        <span>{t('tickets.by')} <span className="font-medium">{ticket.createdByName}</span></span>
        <span>· {t(`tickets.cat.${ticket.category}`)}</span>
        {ticket.horseName && <span>· {t('tickets.about')} {ticket.horseName}</span>}
        <span>· {fmtDate(ticket.createdAt, locale)}</span>
      </div>

      {ticket.message && <p className="mt-3 whitespace-pre-wrap text-sm text-brand-700 dark:text-brand-200">{ticket.message}</p>}

      <div className="mt-4 flex items-center gap-3">
        <a href={`mailto:${ticket.createdByEmail}`} className="text-sm font-medium text-brand-600 dark:text-brand-300 hover:underline">{ticket.createdByEmail}</a>
        <span className="flex-1" />
        {resolved
          ? onReopen && <button disabled={busy} onClick={onReopen} className="btn-ghost !px-4 !py-2 text-xs disabled:opacity-60">{t('tickets.reopen')}</button>
          : onResolve && <button disabled={busy} onClick={onResolve} className="btn-primary !px-4 !py-2 text-xs disabled:opacity-60">{t('tickets.markResolved')}</button>}
      </div>
    </div>
  )
}
